import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Shirt, Umbrella, Sun as SunIcon, Wind, Activity, Thermometer, AlertTriangle } from 'lucide-react';

interface WeatherData {
  temperature: number
  precipitation_probability?: number
  uv_index?: number
  wind_speed?: number
  humidity?: number
}

interface Recommendation {
  type: 'clothing' | 'alert' | 'activity'
  title: string
  description: string
  icon: typeof Shirt
  severity: 'info' | 'warning' | 'danger'
}

interface WeatherRecommendationsProps {
  weather: WeatherData
}

function generateRecommendations(weather: WeatherData, t: (key: string, options?: any) => string): Recommendation[] {
  const recommendations: Recommendation[] = [];
  const temp = weather.temperature;

  // Temperature-based clothing recommendations
  if (temp < 0) {
    recommendations.push({
      type: 'clothing',
      title: t('recommendations.winterClothing.title'),
      description: t('recommendations.winterClothing.desc'),
      icon: Shirt,
      severity: 'warning',
    });
  } else if (temp < 10) {
    recommendations.push({
      type: 'clothing',
      title: t('weather.jacketRecommended'),
      description: t('recommendations.jacket.desc'),
      icon: Shirt,
      severity: 'info',
    });
  } else if (temp >= 30) {
    recommendations.push({
      type: 'clothing',
      title: t('recommendations.lightClothing.title'),
      description: t('recommendations.lightClothing.desc'),
      icon: Shirt,
      severity: 'warning',
    });
  }

  // Rain recommendations
  if (weather.precipitation_probability && weather.precipitation_probability > 50) {
    recommendations.push({
      type: 'alert',
      title: t('weather.bringUmbrella'),
      description: t('recommendations.rainChance', { percent: weather.precipitation_probability }),
      icon: Umbrella,
      severity: 'warning',
    });
  }

  // UV Index recommendations
  if (weather.uv_index) {
    if (weather.uv_index >= 8) {
      recommendations.push({
        type: 'alert',
        title: t('recommendations.highUv.title'),
        description: t('recommendations.highUv.desc'),
        icon: SunIcon,
        severity: 'danger',
      });
    } else if (weather.uv_index >= 6) {
      recommendations.push({
        type: 'alert',
        title: t('recommendations.sunProtection.title'),
        description: t('recommendations.sunProtection.desc'),
        icon: SunIcon,
        severity: 'warning',
      });
    }
  }

  // Activity recommendations
  if (
    temp >= 15 &&
    temp <= 25 &&
    (!weather.precipitation_probability || weather.precipitation_probability < 30) &&
    (!weather.wind_speed || weather.wind_speed < 30)
  ) {
    recommendations.push({
      type: 'activity',
      title: t('recommendations.perfectForSport.title'),
      description: t('recommendations.perfectForSport.desc'),
      icon: Activity,
      severity: 'info',
    });
  }

  // Wind warnings
  if (weather.wind_speed) {
    if (weather.wind_speed > 50) {
      recommendations.push({
        type: 'alert',
        title: t('recommendations.strongWind.title'),
        description: t('recommendations.strongWind.desc'),
        icon: Wind,
        severity: 'danger',
      });
    } else if (weather.wind_speed > 30) {
      recommendations.push({
        type: 'alert',
        title: t('recommendations.windyDay.title'),
        description: t('recommendations.windyDay.desc'),
        icon: Wind,
        severity: 'warning',
      });
    }
  }

  // Temperature extreme warnings
  if (temp < -10) {
    recommendations.push({
      type: 'alert',
      title: t('recommendations.extremeCold.title'),
      description: t('recommendations.extremeCold.desc'),
      icon: Thermometer,
      severity: 'danger',
    });
  } else if (temp > 35) {
    recommendations.push({
      type: 'alert',
      title: t('recommendations.extremeHeat.title'),
      description: t('recommendations.extremeHeat.desc'),
      icon: AlertTriangle,
      severity: 'danger',
    });
  }

  return recommendations;
}

export default function WeatherRecommendations({ weather }: WeatherRecommendationsProps) {
  const { t } = useTranslation();
  const recommendations = generateRecommendations(weather, t);

  if (recommendations.length === 0) {
    return null;
  }

  const severityColors = {
    info: 'bg-info/10 border-info/30 text-info',
    warning: 'bg-warning/10 border-warning/30 text-warning',
    danger: 'bg-danger/10 border-danger/30 text-danger',
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-text-primary">{t('weather.recommendations')}</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        {recommendations.map((rec, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-4 rounded-lg border ${severityColors[rec.severity]}`}
          >
            <div className="flex items-start gap-3">
              <rec.icon className="w-6 h-6 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1">{rec.title}</h3>
                <p className="text-sm opacity-90">{rec.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
